import config from '../../config'

const META_PIXEL_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js'
const PAGE_VIEW_EVENT_NAME = 'PageView'

type MetaPixelTrackMethod = 'track' | 'trackCustom'
type MetaPixelCommand =
  | ['init', string]
  | [MetaPixelTrackMethod, string]
  | [MetaPixelTrackMethod, string, Record<string, unknown>]

type MetaPixelFunction = ((...args: MetaPixelCommand) => void) & {
  callMethod?: (...args: MetaPixelCommand) => void
  loaded?: boolean
  push?: MetaPixelFunction
  queue?: MetaPixelCommand[]
  version?: string
}

declare global {
  interface Window {
    _fbq?: MetaPixelFunction
    fbq?: MetaPixelFunction
  }
}

class MetaPixelClient {
  private initialised = false
  private get fbq() {
    if (window.fbq) {
      return window.fbq
    }
    const queuedCommands: MetaPixelCommand[] = []
    const fbq = ((...args: MetaPixelCommand) => {
      const callMethod = window.fbq?.callMethod
      if (callMethod) {
        callMethod(...args)
        return
      }
      queuedCommands.push(args)
    }) as MetaPixelFunction

    fbq.loaded = true
    fbq.push = fbq
    fbq.queue = queuedCommands
    fbq.version = '2.0'

    window.fbq = fbq
    if (!window._fbq) {
      window._fbq = fbq
    }

    return fbq
  }
  private ensureScript = () => {
    if (document.querySelector(`script[src="${META_PIXEL_SCRIPT_SRC}"]`)) {
      return
    }
    const script = document.createElement('script')
    script.async = true
    script.src = META_PIXEL_SCRIPT_SRC
    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
      return
    }

    document.head.appendChild(script)
  }
  private init = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false
    }
    if (!config.metaPixelId) {
      console.info(`metaPixelId is not configured for this environment.`)
      return false
    }
    this.ensureScript()
    if (!this.initialised) {
      this.fbq('init', config.metaPixelId)
      this.initialised = true
    }
    return true
  }
  private trackEvent = ({
    eventName,
    eventParams,
    custom = false,
  }: {
    eventName: string
    eventParams?: Record<string, unknown>
    custom?: boolean
  }) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false
    }
    if (!this.init()) {
      return false
    }
    const fbq = window.fbq
    if (!fbq) {
      return false
    }
    const trackMethod: MetaPixelTrackMethod = custom ? 'trackCustom' : 'track'
    if (eventParams) {
      fbq(trackMethod, eventName, eventParams)
    } else {
      fbq(trackMethod, eventName)
    }
    return true
  }
  trackPageView = () => this.trackEvent({ eventName: PAGE_VIEW_EVENT_NAME })
}

export default new MetaPixelClient()
